import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { executeRecaptchaEnterprise } from '../lib/recaptchaEnterprise';
import { formatDateEU } from '../utils/dateFormat';

const { FiCheckCircle, FiLoader, FiCalendar, FiMapPin, FiCreditCard, FiTag, FiAlertCircle, FiUploadCloud, FiFile, FiImage, FiX, FiCopy } = FiIcons;

function getSlugFromWindowHash() {
  const hash = window.location.hash || '';
  const match = hash.match(/^#\/register\/([^/?#]+)/);
  if (match) return decodeURIComponent(match[1]);
  return hash.split('/').pop() || '';
}

function normalizeOptions(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }
  return [];
}

function initialValueForField(field) {
  if (field.field_type === 'multiselect') return [];
  if (field.field_type === 'checkbox') return false;
  if (field.field_type === 'consent') return false;
  return '';
}

function isWideField(fieldType) {
  return ['textarea', 'consent', 'checkbox', 'multiselect', 'file', 'image'].includes(fieldType);
}

function fieldInputType(fieldType) {
  switch (fieldType) {
    case 'number': return 'number';
    case 'date': return 'date';
    case 'email': return 'email';
    case 'phone': return 'tel';
    case 'url': return 'url';
    case 'signature': return 'text';
    default: return 'text';
  }
}

function shouldUppercaseField(field) {
  return ['text', 'textarea', 'phone', 'email', 'url', 'signature', 'select', 'yes_no'].includes(field.field_type);
}

function uppercaseFieldValue(field, value) {
  if (!shouldUppercaseField(field)) return value;
  if (typeof value !== 'string') return value;
  return value.toUpperCase();
}

function normalizeDiscountCode(value) {
  return String(value || '').trim().toUpperCase();
}

function extractBirthYear(dateValue) {
  if (!dateValue) return null;
  const match = String(dateValue).match(/^(\d{4})-\d{2}-\d{2}$/);
  if (match) return Number(match[1]);
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.getFullYear();
}

function buildCoreAndAnswers(fields, formValues) {
  const core = {};
  const answers = {};
  fields.forEach((field) => {
    const value = formValues[field.field_key];
    if (field.maps_to_core_field) {
      if (field.maps_to_core_field === 'gender') {
        core[field.maps_to_core_field] = String(value || '').trim().toLowerCase();
      } else {
        core[field.maps_to_core_field] = value;
      }
    } else {
      answers[field.field_key] = value;
    }
  });
  if (core.date_of_birth) { core.birth_year = extractBirthYear(core.date_of_birth); }
  if (core.consent_accepted) { core.agreement_accepted = true; }
  return { core, answers };
}

function getPriceNumber(...values) {
  for (const value of values) {
    if (value === '' || value === null || value === undefined) continue;

    const numberValue = Number(value);

    if (Number.isFinite(numberValue) && numberValue > 0) {
      return numberValue;
    }
  }

  return 0;
}

const SmartDateInput = ({ value, onChange, required, placeholder, className }) => {
  const inputId = React.useId();
  const [isFocused, setIsFocused] = useState(false);
  const showOverlay = !isFocused && !value;

  const openDatePicker = () => {
    const el = document.getElementById(inputId);
    if (!el) return;

    try {
      el.focus();

      if (typeof el.showPicker === 'function') {
        el.showPicker();
      }
    } catch {
      el.focus();
    }
  };

  return (
    <div className="relative">
      <input
        id={inputId}
        type="date"
        value={value || ''}
        required={required}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(e) => onChange(e.target.value)}
        className={className}
      />

      {showOverlay ? (
        <button
          type="button"
          onClick={openDatePicker}
          className="absolute inset-0 flex items-center px-6 text-left font-bold text-gray-400 bg-white rounded-2xl pointer-events-auto"
        >
          {placeholder || 'DD/MM/YYYY'}
        </button>
      ) : null}
    </div>
  );
};

function linkifyText(text) {
  if (!text) return null;

  const combinedRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|\+?\d[\d\s().-]{7,}\d)/gi;

  return String(text).split(combinedRegex).map((part, index) => {
    if (!part) return null;

    const isUrl = /^(https?:\/\/|www\.)/i.test(part);
    const isEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(part);
    const isPhone =
      /^\+?\d[\d\s().-]{7,}\d$/i.test(part) &&
      part.replace(/[^\d]/g, '').length >= 10;

    if (!isUrl && !isEmail && !isPhone) return part;

    let href = part;

    if (isUrl) {
      href = part.startsWith('www.') ? `https://${part}` : part;
    }

    if (isEmail) {
      href = `mailto:${part}`;
    }

    if (isPhone) {
      const digits = part.replace(/[^\d+]/g, '');
      const telValue =
        digits.startsWith('+') ? digits : digits.startsWith('30') ? `+${digits}` : `+30${digits}`;

      href = `tel:${telValue}`;
    }

    return (
      <a
        key={`${part}-${index}`}
        href={href}
        target={isUrl ? '_blank' : undefined}
        rel={isUrl ? 'noopener noreferrer' : undefined}
        className="font-black text-white underline decoration-indigo-300 underline-offset-4 hover:text-indigo-200"
      >
        {part}
      </a>
    );
  });
}

export default function RegistrationForm({ forcedPublicSlug = '' }) {
  const [data, setData] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceLoading, setPriceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formValues, setFormValues] = useState({});
  const [discountCode, setDiscountCode] = useState('');
  const [uploadingFields, setUploadingFields] = useState({});
  const [copiedIbanKey, setCopiedIbanKey] = useState('');
  const [recaptchaConfig, setRecaptchaConfig] = useState(null);

  const slug = forcedPublicSlug || getSlugFromWindowHash();
  const normalizedDiscountCode = useMemo(() => normalizeDiscountCode(discountCode), [discountCode]);

  useEffect(() => {
    async function fetchSecurityConfig() {
      try {
        const publicSettings = await spformsApi.settingsGetPublic();
        setRecaptchaConfig({
          enabled: publicSettings.recaptcha_enterprise_enabled,
          site_key: publicSettings.recaptcha_enterprise_site_key,
          action: publicSettings.recaptcha_enterprise_action_registration || 'registration_submit'
        });
      } catch (e) {
        console.warn('Failed to load public security settings', e);
      }
    }
    fetchSecurityConfig();
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadForm() {
      try {
        setLoading(true);
        setError('');
        setSuccess('');
        const result = await spformsApi.publicFormGet(slug);
        if (!result) { throw new Error('Registration form not found or not active.'); }
        if (!mounted) return;
        const fields = Array.isArray(result.fields) ? result.fields : [];
        const initialValues = {};
        fields.forEach((field) => { initialValues[field.field_key] = initialValueForField(field); });
        setData({ ...result, fields });
        setPriceData(result.pricing || null);
        setFormValues(initialValues);
      } catch (loadError) {
        if (!mounted) return;
        setError(loadError.message || 'Failed to load registration form.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadForm();
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (!data?.group?.id) return;
    const timer = setTimeout(async () => {
      try {
        setPriceLoading(true);
        const result = await spformsApi.calculatePrice(data.group.id, normalizedDiscountCode);
        setPriceData(result);
      } catch (priceError) {
        setPriceData((prev) => ({ ...(prev || {}), discount_valid: false, discount_message: priceError.message || 'Invalid discount code', }));
      } finally {
        setPriceLoading(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [normalizedDiscountCode, data?.group?.id]);

  const fields = useMemo(() => {
    if (!data?.fields) return [];
    return [...data.fields].sort((a, b) => {
      const orderA = Number(a.sort_order || 0);
      const orderB = Number(b.sort_order || 0);
      if (orderA !== orderB) return orderA - orderB;
      return String(a.created_at || '').localeCompare(String(b.created_at || ''));
    });
  }, [data]);

  const group = data?.group || null;
  const bannerUrl = group?.banner_image_url || '';
  const eventBasePrice = getPriceNumber(
    group?.price_amount,
    group?.price,
    group?.event_price,
    group?.registration_fee,
    priceData?.original_price,
    priceData?.regular_price,
    priceData?.base_price,
    priceData?.event_price
  );

  const finalPrice =
    priceData?.final_price === 0 || priceData?.final_price === '0'
      ? 0
      : getPriceNumber(priceData?.final_price, priceData?.base_price, eventBasePrice);

  const showStrikedBasePrice =
    eventBasePrice > finalPrice &&
    eventBasePrice > 0;

  const copyIban = async (iban, key) => {
    if (!iban) return;
    try {
      await navigator.clipboard.writeText(iban);
      setCopiedIbanKey(key);
      setTimeout(() => { setCopiedIbanKey(''); }, 1800);
    } catch {
      alert('Could not copy IBAN. Please copy it manually.');
    }
  };

  const renderBankTransferCard = ({ keyName, enabled, iban, beneficiary, bankName, notes, title, }) => {
    if (!enabled || !iban) return null;
    return (
      <div className="mt-10 rounded-[2rem] border-2 border-emerald-100 bg-white p-6">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600"> {title} </div>
        {beneficiary ? ( <div className="mt-4 text-sm font-bold text-gray-600"> Beneficiary: <span className="font-black text-gray-900">{beneficiary}</span> </div> ) : null}
        {bankName ? ( <div className="mt-2 text-sm font-bold text-gray-600"> Bank: <span className="font-black text-gray-900">{bankName}</span> </div> ) : null}
        <button type="button" onClick={() => copyIban(iban, keyName)} className="mt-5 w-full rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50 px-5 py-4 text-left font-mono text-sm font-black text-emerald-800 hover:bg-emerald-100 transition-all flex items-center justify-between gap-4" >
          <span className="break-all">{iban}</span>
          <SafeIcon icon={FiCopy} />
        </button>
        <div className="mt-2 text-xs font-bold text-emerald-600"> {copiedIbanKey === keyName ? 'IBAN copied!' : 'Click IBAN to copy'} </div>
        {notes ? ( <p className="mt-4 text-xs font-bold leading-6 text-gray-500"> {notes} </p> ) : null}
      </div>
    );
  };

  const handleFieldChange = (field, value) => {
    const nextValue = uppercaseFieldValue(field, value);
    setFormValues((prev) => ({ ...prev, [field.field_key]: nextValue }));
    if (field.maps_to_core_field === 'discount_code' || field.field_key === 'discount_code') {
      setDiscountCode(normalizeDiscountCode(nextValue));
    }
  };

  const handleFileUpload = async (field, event) => {
    const file = event.target.files?.[0];
    if (!file || !group) return;
    try {
      setUploadingFields((prev) => ({ ...prev, [field.field_key]: true }));
      const result = await spformsApi.uploadPublicAsset({ tenantId: group.tenant_id, intakeGroupId: group.id, file, folder: 'answers', });
      handleFieldChange(field, result.url);
    } catch (uploadError) {
      alert(uploadError.message || 'Upload failed');
    } finally {
      setUploadingFields((prev) => ({ ...prev, [field.field_key]: false }));
      event.target.value = '';
    }
  };

  const renderField = (field) => {
    const value = formValues[field.field_key] ?? initialValueForField(field);
    const isUploading = uploadingFields[field.field_key];
    const options = normalizeOptions(field.options_json);
    const baseClass = 'w-full px-6 py-4 rounded-2xl border-2 border-gray-100 font-bold text-gray-900 bg-white outline-none focus:border-indigo-600 transition-all';
    if (field.field_type === 'textarea') { return ( <textarea required={field.required} value={value} onChange={(e) => handleFieldChange(field, e.target.value)} rows={4} className={`${baseClass} uppercase`} placeholder={field.help_text || ''} /> ); }
    if (field.field_type === 'consent') { return ( <label className="flex items-start gap-5 rounded-3xl border-2 border-indigo-100 bg-indigo-50/40 p-6 cursor-pointer hover:bg-indigo-50 transition-all"> <input type="checkbox" checked={Boolean(value)} required={field.required} onChange={(e) => handleFieldChange(field, e.target.checked)} className="mt-1 h-7 w-7 rounded-lg border-2 border-indigo-200 text-indigo-600 focus:ring-indigo-500" /> <span className="text-sm font-bold text-gray-700 leading-relaxed"> {field.help_text || field.label} </span> </label> ); }
    if (field.field_type === 'checkbox') { return ( <label className="flex items-start gap-4 rounded-2xl border-2 border-gray-100 bg-gray-50/50 p-5 cursor-pointer hover:bg-white transition-all"> <input type="checkbox" checked={Boolean(value)} required={field.required} onChange={(e) => handleFieldChange(field, e.target.checked)} className="mt-1 h-6 w-6 rounded-lg border-gray-300 text-indigo-600" /> <span className="text-sm font-bold text-gray-600 leading-relaxed"> {field.help_text || field.label} </span> </label> ); }
    if (field.field_type === 'select') { return ( <select required={field.required} value={value} onChange={(e) => handleFieldChange(field, e.target.value)} className={`${baseClass} uppercase`} > <option value="">CHOOSE OPTION</option> {options.map((option) => ( <option key={option} value={String(option).toUpperCase()}> {String(option).toUpperCase()} </option> ))} </select> ); }
    if (field.field_type === 'multiselect') {
      const selected = Array.isArray(value) ? value : [];
      return ( <div className="flex flex-wrap gap-2"> {options.map((option) => { const normalizedOption = String(option).toUpperCase(); const active = selected.includes(normalizedOption); return ( <button key={normalizedOption} type="button" onClick={() => { const next = active ? selected.filter((item) => item !== normalizedOption) : [...selected, normalizedOption]; handleFieldChange(field, next); }} className={`rounded-xl border px-4 py-2 text-xs font-black uppercase tracking-widest transition-all ${active ? 'border-indigo-600 bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300'}`} > {normalizedOption} </button> ); })} {field.required && selected.length === 0 ? ( <input tabIndex={-1} autoComplete="off" value="" onChange={() => { }} required className="sr-only" /> ) : null} </div> );
    }
    if (field.field_type === 'yes_no') {
      return ( <div className="flex gap-3"> {['YES', 'NO'].map((option) => ( <button key={option} type="button" onClick={() => handleFieldChange(field, option)} className={`rounded-xl border px-8 py-3 text-xs font-black uppercase tracking-widest transition-all ${value === option ? 'border-indigo-600 bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-300'}`} > {option} </button> ))} {field.required && !value ? ( <input tabIndex={-1} autoComplete="off" value="" onChange={() => { }} required className="sr-only" /> ) : null} </div> );
    }
    if (field.field_type === 'file' || field.field_type === 'image') {
      return ( <div className="space-y-4"> <input type="file" accept={field.field_type === 'image' ? 'image/*' : '*'} onChange={(e) => handleFileUpload(field, e)} className="hidden" id={`file-${field.field_key}`} disabled={isUploading} /> <label htmlFor={`file-${field.field_key}`} className={`flex items-center justify-center gap-3 w-full px-6 py-5 rounded-2xl border-2 border-dashed transition-all cursor-pointer ${isUploading ? 'bg-gray-50 border-gray-200 cursor-wait' : value ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-indigo-300 hover:bg-white'}`} > <SafeIcon icon={isUploading ? FiLoader : field.field_type === 'image' ? FiImage : FiUploadCloud} className={isUploading ? 'animate-spin' : ''} /> <span className="font-bold text-sm"> {isUploading ? 'Uploading...' : value ? 'File Uploaded' : 'Select File'} </span> </label> {field.required && !value ? ( <input tabIndex={-1} autoComplete="off" value="" onChange={() => { }} required className="sr-only" /> ) : null} {value ? ( <div className="flex items-center gap-3 bg-white border rounded-xl p-3 shadow-sm"> <SafeIcon icon={FiFile} className="text-indigo-600" /> <a href={value} target="_blank" rel="noreferrer" className="text-[10px] font-mono text-gray-500 truncate flex-1 hover:text-indigo-600" > {value} </a> <button type="button" onClick={() => handleFieldChange(field, '')} className="text-red-500 hover:bg-red-50 p-1 rounded-lg" > <SafeIcon icon={FiX} /> </button> </div> ) : null} </div> );
    }
    if (field.field_type === 'date') { return ( <SmartDateInput required={field.required} value={value} onChange={(val) => handleFieldChange(field, val)} className={baseClass} placeholder={field.help_text || 'DD/MM/YYYY'} /> ); }
    return ( <input type={fieldInputType(field.field_type)} required={field.required} value={value} onChange={(e) => handleFieldChange(field, e.target.value)} className={`${baseClass} ${shouldUppercaseField(field) ? 'uppercase' : ''}`} placeholder={field.help_text || ''} /> );
  };

  const isBlockingRecaptchaError = (err) => {
    return (
      err?.message === 'RECAPTCHA_EXECUTE_FAILED' ||
      err?.message === 'RECAPTCHA_SCRIPT_LOAD_FAILED' ||
      err?.message === 'RECAPTCHA_TOKEN_MISSING'
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError('');

      let recaptchaToken = null;
      let recaptchaAction = null;

      if (recaptchaConfig) {
        try {
          const result = await executeRecaptchaEnterprise(recaptchaConfig.action, recaptchaConfig);
          if (result.status === 'OK') {
            recaptchaToken = result.token;
            recaptchaAction = result.action;
          }
        } catch (err) {
          if (isBlockingRecaptchaError(err)) {
            throw new Error('Security verification failed. Please refresh the page and try again.');
          }
        }
      }

      const { core, answers } = buildCoreAndAnswers(fields, formValues);
      const registrationResult = await spformsApi.registrationCreatePublic({
        public_slug: slug,
        core,
        answers,
        discount_code: normalizedDiscountCode,
        recaptcha_token: recaptchaToken,
        recaptcha_action: recaptchaAction
      });

      if (typeof spformsApi.sendRegistrationConfirmationEmail === 'function') {
        try {
          await spformsApi.sendRegistrationConfirmationEmail({ public_slug: slug, registration_id: registrationResult?.id || registrationResult?.registration_id || null, core, answers, discount_code: normalizedDiscountCode, final_price: finalPrice, group, });
        } catch (emailError) {
          console.error('Confirmation email failed, but registration was saved:', emailError);
        }
      }
      setSuccess('Application submitted! We have received your registration.');
      window.scrollTo(0, 0);
    } catch (submitError) {
      setError(submitError.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-24 text-center flex flex-col items-center gap-6">
        <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs font-black uppercase tracking-[0.4em] text-indigo-600 animate-pulse"> Initializing Portal </span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-20 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiAlertCircle} className="text-red-500" size={40} />
        </div>
        <h2 className="text-2xl font-black text-gray-900 mb-2"> Registration unavailable </h2>
        <div className="font-bold text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <div className="bg-indigo-900 p-10 md:p-16 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 p-20 opacity-10"> <SafeIcon icon={FiCalendar} size={200} /> </div>
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-300 mb-4"> Official Registration Form </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-none"> {group.title} </h1>
          {bannerUrl ? (
            <div className="mt-10 overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 shadow-2xl shadow-indigo-950/30">
              <img src={bannerUrl} className="w-full h-auto object-contain" alt={group.title} />
            </div>
          ) : null}
          <div className="mt-8 flex flex-wrap gap-4">
            {group.location ? ( <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10"> <SafeIcon icon={FiMapPin} /> <span className="text-sm font-bold">{group.location}</span> </div> ) : null}
            {group.start_date ? ( <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10"> <SafeIcon icon={FiCalendar} /> <span className="text-sm font-bold"> {formatDateEU(group.start_date)} {group.end_date ? ` - ${formatDateEU(group.end_date)}` : ''} </span> </div> ) : null}
          </div>
          {group.notes ? (
            <p className="mt-8 max-w-3xl whitespace-pre-line text-sm font-medium leading-7 text-indigo-100/80">
              {linkifyText(group.notes)}
            </p>
          ) : null}
        </div>
      </div>
      <div className="p-8 md:p-20">
        {success ? (
          <div className="max-w-4xl mx-auto text-center p-20 bg-emerald-50 rounded-[3rem] border-4 border-emerald-100 animate-in fade-in zoom-in duration-500">
            <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center text-white mx-auto mb-10 shadow-2xl shadow-emerald-200"> <SafeIcon icon={FiCheckCircle} size={48} /> </div>
            <h2 className="text-4xl font-black text-emerald-900 mb-4"> You’re Registered! </h2>
            <p className="text-emerald-700 text-lg font-bold">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-16">
            {error ? ( <div className="rounded-2xl border-2 border-red-100 bg-red-50 p-6 text-sm font-bold text-red-700 flex items-center gap-3"> <SafeIcon icon={FiAlertCircle} /> {error} </div> ) : null}
            {fields.length === 0 ? (
              <div className="rounded-3xl border-2 border-amber-100 bg-amber-50 p-8 text-center">
                <h2 className="text-xl font-black text-amber-900"> No form fields configured </h2>
                <p className="mt-2 text-sm font-bold text-amber-700"> Please ask the organizer to configure this registration form. </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                {fields.map((field) => (
                  <div key={field.id || field.field_key} className={`space-y-3 ${isWideField(field.field_type) ? 'md:col-span-2' : ''}`} >
                    {field.field_type !== 'checkbox' && field.field_type !== 'consent' ? (
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] flex items-center justify-between">
                        <span> {field.label} {field.required ? <span className="text-red-400"> *</span> : null} </span>
                      </label>
                    ) : null}
                    {renderField(field)}
                  </div>
                ))}
              </div>
            )}
            <section className="bg-gray-50 rounded-[3rem] p-10 md:p-14 border-4 border-gray-100 shadow-inner">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-12">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center text-indigo-600"> <SafeIcon icon={FiCreditCard} size={24} /> </div>
                    <h3 className="text-2xl font-black tracking-tight leading-none"> Registration Fee </h3>
                  </div>
                  <div>
                    {showStrikedBasePrice ? ( <div className="mb-2 text-2xl md:text-3xl font-black text-gray-400 tracking-tighter line-through"> €{eventBasePrice.toFixed(2)} </div> ) : null}
                    <div className={`text-5xl md:text-6xl font-black text-gray-900 tracking-tighter ${priceLoading ? 'opacity-40' : 'opacity-100'} transition-opacity`} > €{Number(finalPrice || 0).toFixed(2)} </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {priceData?.early_bird_applied ? ( <span className="inline-flex text-[10px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-100 px-3 py-1 rounded-full"> Early Bird Active </span> ) : null}
                      {priceData?.discount_valid && priceData.discount_code ? ( <span className="inline-flex text-[10px] font-black text-indigo-600 uppercase tracking-widest bg-indigo-100 px-3 py-1 rounded-full"> Code {priceData.discount_code} Applied </span> ) : null}
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-72 space-y-3">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    <SafeIcon icon={FiTag} />
                    Promo Code
                  </label>

                  <input
                    type="text"
                    inputMode="text"
                    enterKeyHint="done"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="Enter Coupon"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    onBlur={() => setDiscountCode(normalizedDiscountCode)}
                    className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 font-black uppercase tracking-widest text-sm focus:border-indigo-600 transition-all outline-none bg-white"
                  />

                  {normalizedDiscountCode && priceData?.discount_valid === false ? (
                    <p className="text-[10px] text-red-500 font-bold uppercase tracking-tight pl-2">
                      {priceData?.discount_message || 'Invalid code'}
                    </p>
                  ) : null}

                  {slug === 'bundesliga-tryouts-2026' && (
                    <div className="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50 p-4 space-y-3">
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-pink-600 mb-1">
                          For Girls
                        </div>
                        <div className="text-sm font-bold text-gray-700">
                          Use code <span className="font-black text-pink-600">GIRL26</span><br />
                          €250 / Early Bird €220
                        </div>
                      </div>

                      <div className="border-t border-indigo-100 pt-3">
                        <div className="text-[10px] font-black uppercase tracking-widest text-blue-600 mb-1">
                          For Teams / Groups
                        </div>
                        <div className="text-sm font-bold text-gray-700">
                          Use code <span className="font-black text-blue-600">TEAM26</span><br />
                          €250 / Early Bird €220
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {renderBankTransferCard({ keyName: 'bank1', enabled: group?.wire_transfer_enabled, iban: group?.wire_transfer_iban, beneficiary: group?.wire_transfer_beneficiary, bankName: group?.wire_transfer_bank_name, notes: group?.wire_transfer_notes, title: 'Bank Transfer Details 1', })}
              {renderBankTransferCard({ keyName: 'bank2', enabled: group?.wire_transfer_2_enabled, iban: group?.wire_transfer_2_iban, beneficiary: group?.wire_transfer_2_beneficiary, bankName: group?.wire_transfer_2_bank_name, notes: group?.wire_transfer_2_notes, title: 'Bank Transfer Details 2', })}
              <button type="submit" disabled={submitting || fields.length === 0} className="w-full mt-16 bg-indigo-600 text-white py-7 rounded-[2.5rem] text-xl md:text-2xl font-black uppercase tracking-[0.2em] shadow-2xl shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-1 transition-all active:scale-95 flex items-center justify-center gap-5 disabled:opacity-60" >
                {submitting ? ( <SafeIcon icon={FiLoader} className="animate-spin" size={28} /> ) : ( 'Submit Application' )}
              </button>
            </section>
          </form>
        )}

        {recaptchaConfig?.enabled === 'true' ? (
          <p className="mt-12 text-[10px] text-center text-gray-400 leading-relaxed max-w-xl mx-auto">
            This site is protected by reCAPTCHA Enterprise and the Google <br />
            <a href="https://policies.google.com/privacy" className="underline" target="_blank" rel="noreferrer">Privacy Policy</a> and
            <a href="https://policies.google.com/terms" className="underline mx-1" target="_blank" rel="noreferrer">Terms of Service</a> apply.
          </p>
        ) : null}
      </div>
      <footer className="mt-auto border-t border-gray-100 bg-gray-50 py-8 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold text-gray-400 leading-relaxed"> Sportiko Forms is part of <a href="https://sportiko.eu" target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-700 hover:underline transition-colors" > Sportiko.eu </a> — created by <a href="https://codevaro.eu" target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-700 hover:underline transition-colors" > Codevaro.eu </a> </p>
        </div>
      </footer>
    </div>
  );
}